import { BREAKPOINTS } from '@/constants/breakpoints';
import { BreakpointsType, ColTypes } from '@/constants/common';

export type BreakpointEntriesType = [string, unknown];

export const sortBreakpointsBySize = (
  breakpointEntries: BreakpointEntriesType[]
) => {
  return breakpointEntries
    .filter(item => BREAKPOINTS[item[0] as BreakpointsType])
    .sort((a, b) => {
      const firstPoint = BREAKPOINTS?.[a[0] as BreakpointsType];
      const secondPoint = BREAKPOINTS?.[b[0] as BreakpointsType];

      return (
        Number(firstPoint?.replace('px', '')) -
        Number(secondPoint?.replace('px', ''))
      );
    });
};

const generateProperties = (
  properties: Record<string, string | number>
): string => {
  return Object.entries(properties)
    .map(
      ([prop, value]) =>
        `${prop}: ${typeof value === 'number' ? `${value}px` : value};`
    )
    .join('\n');
};

const createMediaQuery = (
  key: BreakpointsType,
  properties: Record<string, string | number>,
  isLast: boolean
) => {
  const breakpoint = BREAKPOINTS[key];
  const maxWidthQuery = `
    @media (max-width: ${breakpoint}) {
      ${generateProperties(properties)}
    }
  `;

  const minWidthQuery = isLast
    ? `
    @media (min-width: ${breakpoint}) {
      ${generateProperties(properties)}
    }`
    : '';

  return `${minWidthQuery}${maxWidthQuery}`;
};

export const buildResponsiveGridMediaQueries = (
  breakpoints: ColTypes,
  properties: string[]
) => {
  const breakpointEntries = Object.entries(breakpoints);

  const transformedEntries = breakpointEntries.map(([key, value]) => {
    const computedValue = Object.fromEntries(
      properties.map(item => [item, value])
    );
    return [key, computedValue] as BreakpointEntriesType;
  });

  const sortedBreakpoints = sortBreakpointsBySize(transformedEntries);

  const mediaQueries = sortedBreakpoints
    .map(([key, property], i, currArr) => {
      const isLast = currArr.length - 1 === i;
      return createMediaQuery(
        key as BreakpointsType,
        property as Record<string, string | number>,
        isLast
      );
    })
    .reverse()
    .join('\n');

  return mediaQueries;
};
